import { Link } from '@backstage/core-components';
import Typography from '@material-ui/core/Typography';
import {
  pendingByEnvironment,
  type PendingPromotion,
  type ReleaseLine,
} from '@internal/backstage-plugin-release-common';

/**
 * What an environment is about to run, once its gitops pull requests merge.
 *
 * The version beside it is what the overlay holds now. Between a candidate
 * being tagged and its pull request being approved, that is the old version —
 * this line is what tells the reader the new one is already built and waiting
 * on a merge, and where to go to give it.
 */
export const PendingPromotions = ({
  pending,
  environment,
}: {
  pending: PendingPromotion[];
  /** Only this environment's; all of them, each named, when absent. */
  environment?: string;
}) => {
  const groups = pendingByEnvironment(pending).filter(
    group => !environment || group.environment === environment,
  );
  if (groups.length === 0) {
    return null;
  }
  return (
    <>
      {groups.map(group => (
        // A caption on one line: it qualifies the version above it rather
        // than competing with it, and a pull request number split from its
        // neighbour across a wrap reads as two unrelated things.
        <Typography
          key={`${group.environment} ${group.version}`}
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ whiteSpace: 'nowrap' }}
        >
          {environment ? '' : `${group.environment}: `}→ {group.version} ·
          awaiting merge{' '}
          {group.pulls.map((pull, index) => (
            <span key={pull.number}>
              {index > 0 && ' '}
              <Link to={pull.url}>#{pull.number}</Link>
            </span>
          ))}
        </Typography>
      ))}
    </>
  );
};

/**
 * The release line in flight: its branch, its latest candidate, and its pull
 * request into `main` while that waits on a review — the step between staging
 * and production that the deployed versions cannot show.
 */
export const ReleaseLineSummary = ({ line }: { line: ReleaseLine | null | undefined }) => {
  if (!line) {
    return <>—</>;
  }
  const latest = line.candidates[line.candidates.length - 1];
  return (
    <>
      {line.branch}
      {latest && ` · ${latest}`}
      {line.pullRequest && (
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ whiteSpace: 'nowrap' }}
        >
          release PR <Link to={line.pullRequest.url}>#{line.pullRequest.number}</Link>{' '}
          awaiting review
        </Typography>
      )}
    </>
  );
};
